/**
 * PreviewPlayer Component
 * 
 * Video preview with playback controls.
 * Handles virtual playback across multiple clips.
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { PreviewPlayerProps } from '../types';
import { getClipAtPosition, formatTime, formatTimePrecise, calculateTotalDuration } from '../utils/clipUtils';
import { logoMarkXml, logoFullXml } from '../../../assets/logoXml';
import ScoreOverlayPreview from './ScoreOverlayPreview';

const PreviewPlayer: React.FC<PreviewPlayerProps> = ({
  clips,
  playheadPosition,
  isPlaying,
  onPlayheadChange,
  onPlayingChange,
  videoUrl,
  hasStitchedVideo = false,
  playbackMode = 'segments',
  onPlaybackModeChange,
  overlayConfig,
  matchId,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stitchedVideoRef = useRef<HTMLVideoElement>(null);
  const [currentClipIndex, setCurrentClipIndex] = useState<number>(-1);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [stitchedVideoTime, setStitchedVideoTime] = useState(0);
  const animationFrameRef = useRef<number>();
  
  // Use refs to avoid stale closures in animation loop
  const clipsRef = useRef(clips);
  const currentClipIndexRef = useRef(currentClipIndex);
  
  useEffect(() => { clipsRef.current = clips; }, [clips]);
  useEffect(() => { currentClipIndexRef.current = currentClipIndex; }, [currentClipIndex]);

  const totalDuration = calculateTotalDuration(clips);

  // Get current clip info based on playhead position - memoize to prevent unnecessary recalculations
  const clipInfo = useMemo(() => {
    if (playbackMode === 'video' || clips.length === 0) return null;
    return getClipAtPosition(clips, playheadPosition);
  }, [clips, playheadPosition, playbackMode]);

  // REMOVED: playbackRate enforcement - production code doesn't have this and works smoothly

  // Update video source and time when clip changes (only when NOT playing to avoid conflicts)
  // Matches production code exactly
  // Skip this effect when in stitched video mode
  // IMPORTANT: clipInfo is memoized, so this won't run unnecessarily
  useEffect(() => {
    if (!videoRef.current || !clipInfo || isPlaying || playbackMode === 'video') return;

    const { clip, positionInClip, clipIndex } = clipInfo;

    if (clipIndex !== currentClipIndex) {
      setCurrentClipIndex(clipIndex);
      videoRef.current.src = clip.sourceVideoUrl;
      videoRef.current.currentTime = positionInClip;
    }
  }, [clipInfo, currentClipIndex, isPlaying, playbackMode]);

  // Handle playback state changes (matches production code exactly)
  // Only applies to segments mode
  // IMPORTANT: Do NOT include playheadPosition in dependencies - it updates every 100ms and causes slow playback
  useEffect(() => {
    if (!videoRef.current || clips.length === 0 || playbackMode === 'video' || isModeSwitchingRef.current) return;

    if (isPlaying) {
      // When starting playback, ensure we're on the right clip
      // Use playheadPositionRef to get current position without causing re-renders
      const currentPos = playheadPosition;
      const info = getClipAtPosition(clips, currentPos);
      if (info) {
        if (info.clipIndex !== currentClipIndex) {
          setCurrentClipIndex(info.clipIndex);
          videoRef.current.src = info.clip.sourceVideoUrl;
        }
        videoRef.current.currentTime = info.positionInClip;
        videoRef.current.play().catch(() => {
          // If play fails, pause
          onPlayingChange(false);
        });
      }
    } else {
      videoRef.current.pause();
    }
  }, [isPlaying, playbackMode, clips.length, currentClipIndex, onPlayingChange]);

  // REMOVED: Aggressive playbackRate enforcement interval
  // This was likely causing slow playback. ClipPlayer (Event Clips) doesn't enforce playbackRate at all
  // and plays at normal speed. We'll only set it on initial load and clip changes, not continuously.

  // Animation frame loop for updating playhead during playback
  // Use ref to throttle state updates - only update state every 100ms to prevent excessive re-renders
  const lastPlayheadUpdateRef = useRef<number>(0);
  const playheadUpdateInterval = 100; // Update state every 100ms (10 times per second)
  const onPlayheadChangeRef = useRef(onPlayheadChange);
  const onPlayingChangeRef = useRef(onPlayingChange);
  const totalDurationRef = useRef(totalDuration);
  
  // Keep refs updated
  useEffect(() => {
    onPlayheadChangeRef.current = onPlayheadChange;
  }, [onPlayheadChange]);
  
  useEffect(() => {
    onPlayingChangeRef.current = onPlayingChange;
  }, [onPlayingChange]);
  
  useEffect(() => {
    totalDurationRef.current = totalDuration;
  }, [totalDuration]);
  
  useEffect(() => {
    if (!isPlaying || clips.length === 0 || playbackMode === 'video') return;

    const updatePlayhead = () => {
      const video = videoRef.current;
      if (!video) return;

      const clipIdx = currentClipIndexRef.current;
      const allClips = clipsRef.current;
      
      if (clipIdx < 0 || clipIdx >= allClips.length) {
        animationFrameRef.current = requestAnimationFrame(updatePlayhead);
        return;
      }
      
      const clip = allClips[clipIdx];

      // Check if we've reached end of current clip
      if (video.currentTime >= clip.endTime) {
        const nextClipIndex = clipIdx + 1;
        
        if (nextClipIndex < allClips.length) {
          // Move to next clip
          const nextClip = allClips[nextClipIndex];
          setCurrentClipIndex(nextClipIndex);
          currentClipIndexRef.current = nextClipIndex;
          
          video.src = nextClip.sourceVideoUrl;
          video.currentTime = nextClip.startTime;
          video.play().catch(() => {});
          
          // Calculate and update playhead position (always update on clip change)
          let nextClipStart = 0;
          for (let i = 0; i < nextClipIndex; i++) {
            nextClipStart += allClips[i].endTime - allClips[i].startTime;
          }
          onPlayheadChangeRef.current(nextClipStart);
          lastPlayheadUpdateRef.current = performance.now();
        } else {
          // End of all clips
          onPlayingChangeRef.current(false);
          onPlayheadChangeRef.current(0);
          return;
        }
      } else {
        // Calculate timeline position from video current time
        let timelinePosition = 0;
        for (let i = 0; i < clipIdx; i++) {
          timelinePosition += allClips[i].endTime - allClips[i].startTime;
        }
        timelinePosition += video.currentTime - clip.startTime;
        const newPosition = Math.min(Math.max(0, timelinePosition), totalDurationRef.current);
        
        // Throttle state updates to prevent excessive re-renders
        const now = performance.now();
        if (now - lastPlayheadUpdateRef.current >= playheadUpdateInterval) {
          onPlayheadChangeRef.current(newPosition);
          lastPlayheadUpdateRef.current = now;
        }
      }

      animationFrameRef.current = requestAnimationFrame(updatePlayhead);
    };

    animationFrameRef.current = requestAnimationFrame(updatePlayhead);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, clips.length, playbackMode]);

  // Handle play/pause toggle
  const togglePlay = useCallback(() => {
    if (clips.length === 0) return;
    
    // If at end, restart from beginning
    if (playheadPosition >= totalDuration - 0.1) {
      onPlayheadChange(0);
    }
    
    onPlayingChange(!isPlaying);
  }, [clips.length, playheadPosition, totalDuration, isPlaying, onPlayheadChange, onPlayingChange]);

  // Handle seek
  const handleSeek = useCallback((newPosition: number) => {
    onPlayheadChange(Math.max(0, Math.min(newPosition, totalDuration)));
    
    const newClipInfo = getClipAtPosition(clips, newPosition);
    if (newClipInfo && videoRef.current) {
      if (newClipInfo.clipIndex !== currentClipIndex) {
        videoRef.current.src = newClipInfo.clip.sourceVideoUrl;
        setCurrentClipIndex(newClipInfo.clipIndex);
      }
      videoRef.current.currentTime = newClipInfo.positionInClip;
    }
  }, [clips, currentClipIndex, totalDuration, onPlayheadChange]);

  // Skip forward/backward
  const skip = useCallback((seconds: number) => {
    handleSeek(playheadPosition + seconds);
  }, [playheadPosition, handleSeek]);

  // Volume control
  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  // Watermark overlay configuration - MUST be before any early returns (React Hooks rules)
  const watermarkConfig = overlayConfig?.watermark;
  const showWatermark = watermarkConfig?.enabled === true;
  
  // Get logo SVG - use logoData from config, or fallback based on logoType
  const logoSvg = useMemo(() => {
    if (watermarkConfig?.logoData) {
      return watermarkConfig.logoData;
    }
    // Fallback to logoType or default to mark (favicon)
    const logoType = watermarkConfig?.logoType || 'mark';
    return logoType === 'full' ? logoFullXml : logoMarkXml;
  }, [watermarkConfig?.logoData, watermarkConfig?.logoType]);

  // Calculate watermark size based on config and logo type
  // Full logo (with text) is wider, so needs different sizing than mark (icon only)
  const watermarkSize = useMemo(() => {
    const logoType = watermarkConfig?.logoType || 'mark';
    const size = watermarkConfig?.size || 'medium';
    
    if (logoType === 'full') {
      // Full logo is wide (aspect ratio ~3.35:1), so use width-based sizing
      const sizeMap: Record<'small' | 'medium' | 'large', string> = {
        small: 'w-24 h-auto',   // ~96px wide, auto height
        medium: 'w-32 h-auto',  // ~128px wide, auto height
        large: 'w-40 h-auto',   // ~160px wide, auto height
      };
      return sizeMap[size as 'small' | 'medium' | 'large'];
    } else {
      // Mark logo is square, use fixed width/height
      const sizeMap: Record<'small' | 'medium' | 'large', string> = {
        small: 'w-12 h-12',
        medium: 'w-16 h-16',
        large: 'w-20 h-20',
      };
      return sizeMap[size as 'small' | 'medium' | 'large'];
    }
  }, [watermarkConfig?.size, watermarkConfig?.logoType]);

  // Get aspect ratio from overlay config
  const aspectRatio = overlayConfig?.aspectRatio || 'original';

  // Helper function to get aspect ratio CSS value
  const getAspectRatioCSS = (ratio: string): string | undefined => {
    const ratioMap: Record<string, string> = {
      'original': 'auto',
      '16:9': '16/9',
      '9:16': '9/16',
      '1:1': '1/1',
      '4:3': '4/3',
    };
    const cssValue = ratioMap[ratio] || 'auto';
    return cssValue === 'auto' ? undefined : cssValue;
  };


  // Determine which video to show based on playback mode
  const showStitchedVideo = playbackMode === 'video' && videoUrl;


  // Calculate watermark position styles
  // Positioned relative to the aspect-ratio-constrained container (not original video)
  // Adjust padding based on aspect ratio
  const watermarkPosition = useMemo(() => {
    const basePosition = watermarkConfig?.position || 'bottom-right';
    type PositionKey = 'top-left' | 'top-right' | 'top' | 'bottom-left' | 'bottom-right' | 'bottom';
    
    // Position map including top/bottom centered options
    const positionMap: Record<PositionKey, string> = {
      'top-left': 'top-4 left-4',
      'top-right': 'top-4 right-4',
      'top': 'top-4 left-1/2 -translate-x-1/2', // Centered at top
      'bottom-left': 'bottom-4 left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom': 'bottom-4 left-1/2 -translate-x-1/2', // Centered at bottom
    };

    // Adjust padding for vertical (9:16) - reduce horizontal padding
    if (aspectRatio === '9:16') {
      const verticalPositionMap: Record<PositionKey, string> = {
        'top-left': 'top-4 left-2',
        'top-right': 'top-4 right-2',
        'top': 'top-4 left-1/2 -translate-x-1/2',
        'bottom-left': 'bottom-4 left-2',
        'bottom-right': 'bottom-4 right-2',
        'bottom': 'bottom-4 left-1/2 -translate-x-1/2',
      };
      const key = basePosition as PositionKey;
      return verticalPositionMap[key] || positionMap[key] || positionMap['bottom-right'];
    }

    // Adjust padding for square (1:1) - balanced padding
    if (aspectRatio === '1:1') {
      const squarePositionMap: Record<PositionKey, string> = {
        'top-left': 'top-3 left-3',
        'top-right': 'top-3 right-3',
        'top': 'top-3 left-1/2 -translate-x-1/2',
        'bottom-left': 'bottom-3 left-3',
        'bottom-right': 'bottom-3 right-3',
        'bottom': 'bottom-3 left-1/2 -translate-x-1/2',
      };
      const key = basePosition as PositionKey;
      return squarePositionMap[key] || positionMap[key] || positionMap['bottom-right'];
    }

    // Default for original, 16:9, 4:3
    const key = basePosition as PositionKey;
    return positionMap[key] || positionMap['bottom-right'];
  }, [watermarkConfig?.position, aspectRatio]);

  // Track stitched video time for score overlay updates
  useEffect(() => {
    if (!showStitchedVideo || !stitchedVideoRef.current) return;

    const video = stitchedVideoRef.current;
    
    const updateTime = () => {
      if (video) {
        setStitchedVideoTime(video.currentTime);
      }
    };

    // Update time on timeupdate event (fires during playback)
    video.addEventListener('timeupdate', updateTime);
    // Also update on seek
    video.addEventListener('seeked', updateTime);
    // Update on load
    video.addEventListener('loadedmetadata', updateTime);

    return () => {
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('seeked', updateTime);
      video.removeEventListener('loadedmetadata', updateTime);
    };
  }, [showStitchedVideo]);

  // Track previous playback mode to detect mode switches
  const prevPlaybackModeRef = useRef(playbackMode);
  const isModeSwitchingRef = useRef(false);
  
  // Reset video state when switching modes
  useEffect(() => {
    const modeChanged = prevPlaybackModeRef.current !== playbackMode;
    prevPlaybackModeRef.current = playbackMode;

    if (playbackMode === 'segments' && modeChanged) {
      // When switching to segments mode, ensure video is ready and reset clip index
      isModeSwitchingRef.current = true;
      
      if (videoRef.current && clips.length > 0) {
        const info = getClipAtPosition(clips, playheadPosition);
        if (info) {
          setCurrentClipIndex(info.clipIndex);
          videoRef.current.src = info.clip.sourceVideoUrl;
          videoRef.current.currentTime = info.positionInClip;
          
          // Wait for video to load, then restore playing state if it was playing
          const handleLoadedData = () => {
            if (videoRef.current && isPlaying) {
              videoRef.current.play().catch(() => {
                onPlayingChange(false);
              });
            }
            isModeSwitchingRef.current = false;
            videoRef.current?.removeEventListener('loadeddata', handleLoadedData);
          };
          
          videoRef.current.addEventListener('loadeddata', handleLoadedData);
          
          // If video is already loaded, handle immediately
          if (videoRef.current.readyState >= 2) {
            if (isPlaying) {
              videoRef.current.play().catch(() => {
                onPlayingChange(false);
              });
            }
            isModeSwitchingRef.current = false;
          }
        }
      }
    } else if (playbackMode === 'video' && modeChanged) {
      // When switching to stitched video mode, reset stitched video time and ensure video loads
      setStitchedVideoTime(0);
      isModeSwitchingRef.current = false;
      
      // Ensure the stitched video loads the correct URL
      if (stitchedVideoRef.current && videoUrl) {
        // Force reload if src has changed
        if (stitchedVideoRef.current.src !== videoUrl) {
          stitchedVideoRef.current.src = videoUrl;
          stitchedVideoRef.current.load();
        }
      }
    }
  }, [playbackMode, clips, playheadPosition, isPlaying, onPlayingChange, videoUrl]);

  // No clips state
  if (clips.length === 0) {
    return (
      <div className="flex flex-col h-full bg-gray-100">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-400 text-sm">Add clips to preview</p>
          </div>
        </div>
        <div className="h-12 bg-white border-t border-gray-200" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Playback Mode Toggle - Always visible, disabled if no stitched video */}
      {onPlaybackModeChange && (
        <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-center">
          <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onPlaybackModeChange) {
                  onPlaybackModeChange('segments');
                }
              }}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                playbackMode === 'segments'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Segments
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onPlaybackModeChange) {
                  onPlaybackModeChange('video');
                }
              }}
              disabled={!hasStitchedVideo}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                playbackMode === 'video'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              } ${
                !hasStitchedVideo
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
            >
              Stitched Video
            </button>
          </div>
        </div>
      )}

      {/* Video Display */}
      <div className="flex-1 relative flex items-center justify-center bg-gray-800 rounded-lg m-2 overflow-hidden">
        {/* Video wrapper - relative container for video and watermark */}
        {/* Note: In preview, watermark may appear in letterbox areas due to container sizing.
            In final processed video, the worker applies watermark directly to video frames,
            so it will be properly overlaid on the actual video content, not letterbox areas. */}
        <div 
          className="relative flex items-center justify-center"
          style={{
            ...(aspectRatio !== 'original' && {
              aspectRatio: getAspectRatioCSS(aspectRatio),
              // For vertical/tall ratios (9:16), constrain by height to fill available height
              // For square ratios (1:1) and classic ratios (4:3), also constrain by height for consistent behavior
              // For widescreen ratios (16:9), constrain by width to fill available width
              width: ['9:16', '1:1', '4:3'].includes(aspectRatio) ? 'auto' : '100%',
              height: ['9:16', '1:1', '4:3'].includes(aspectRatio) ? '100%' : 'auto',
              maxWidth: '100%',
              maxHeight: '100%',
              // Ensure container maintains aspect ratio
              flexShrink: 0,
            }),
            ...(aspectRatio === 'original' && {
              width: '100%',
              height: '100%',
            }),
            position: 'relative',
          }}
        >
          {/* Video element - will be cropped to fit aspect ratio */}
          <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
            {showStitchedVideo ? (
              <video
                key={`stitched-${videoUrl}`}
                ref={stitchedVideoRef}
                src={videoUrl || undefined}
                controls
                className="relative"
                style={{
                  objectFit: 'cover', // Always cover to show what will be cropped
                  width: '100%',
                  height: '100%',
                }}
                playsInline
                onLoadedMetadata={(e) => {
                  // Phase 1: Ensure playback rate is always 1.0
                  e.currentTarget.playbackRate = 1.0;
                  setStitchedVideoTime(e.currentTarget.currentTime);
                }}
              />
            ) : (
        <video
          ref={videoRef}
                className="relative"
                style={{
                  objectFit: 'cover', // Always cover to show what will be cropped
                  width: '100%',
                  height: '100%',
                }}
          playsInline
                disableRemotePlayback
                disablePictureInPicture
          onClick={togglePlay}
        />
            )}
          </div>


          {/* Watermark Overlay - positioned relative to video wrapper container
              Note: Preview positioning may vary due to letterboxing, but final processed
              video will have watermark correctly overlaid on video content via FFmpeg */}
          {showWatermark && (
            <div
              className={`absolute ${watermarkPosition} ${watermarkSize} pointer-events-none z-10`}
              style={{
                opacity: watermarkConfig?.opacity ?? 0.8,
              }}
            >
              <div
                className="w-full h-full"
                dangerouslySetInnerHTML={{ __html: logoSvg }}
              />
            </div>
          )}

          {/* Score Overlay Preview */}
          {overlayConfig?.scoreOverlay?.enabled && (
            <ScoreOverlayPreview
              config={overlayConfig.scoreOverlay}
              matchId={matchId}
              clips={clips}
              currentTime={showStitchedVideo ? stitchedVideoTime : playheadPosition}
              playbackMode={playbackMode}
              aspectRatio={aspectRatio}
            />
          )}

        </div>
        
        {/* Play overlay when paused (only for segments mode) */}
        {!showStitchedVideo && !isPlaying && (
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer z-20"
            onClick={togglePlay}
          >
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm hover:bg-white/30 transition-colors">
              <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}

        {/* Current clip info (only for segments mode) */}
        {!showStitchedVideo && clipInfo && (
          <div className="absolute top-3 left-3 px-2 py-1 bg-black/60 rounded text-xs text-white z-20">
            {clipInfo.clip.label || `Clip ${clipInfo.clipIndex + 1}`}
          </div>
        )}
        
        {/* Preview gaps notice (only for segments mode) */}
        {!showStitchedVideo && clips.length > 1 && (
          <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/50 rounded text-xs text-white/70 flex items-center space-x-1 z-20">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Preview may have gaps between clips</span>
          </div>
        )}
      </div>

      {/* Controls - Only show for segments mode */}
      {playbackMode === 'segments' && (
      <div className="bg-white border-t border-gray-200 px-4 py-2">
        {/* Progress Bar */}
        <div className="mb-2">
          <input
            type="range"
            min="0"
            max={totalDuration}
            step="0.1"
            value={playheadPosition}
            onChange={(e) => handleSeek(parseFloat(e.target.value))}
            className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
        </div>

        {/* Control Buttons */}
        <div className="flex items-center justify-between">
          {/* Left: Time Display */}
          <div className="text-xs text-gray-500 font-mono w-32">
            {formatTimePrecise(playheadPosition)} / {formatTime(totalDuration)}
          </div>

          {/* Center: Playback Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => skip(-5)}
              className="p-1.5 text-gray-500 hover:text-gray-700 transition-colors"
              title="Back 5s"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.334 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
              </svg>
            </button>

            <button
              onClick={togglePlay}
              className="p-2 bg-blue-600 rounded-full text-white hover:bg-blue-700 transition-colors"
            >
              {isPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            <button
              onClick={() => skip(5)}
              className="p-1.5 text-gray-500 hover:text-gray-700 transition-colors"
              title="Forward 5s"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
              </svg>
            </button>
          </div>

          {/* Right: Volume */}
          <div className="flex items-center space-x-2 w-32 justify-end">
            <button
              onClick={toggleMute}
              className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isMuted || volume === 0 ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="w-16 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            />
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default PreviewPlayer;

